import "./DirectorsInfoCardBlock.scss";
import {
  ButtonWithIcon,
  ContentArea,
  Link,
  MinusCircleIcon,
  Picture,
  PlusCircleIcon,
} from "/App.Components";
import { DirectorsInfoCardBlockProps } from "/Models/Generated/DirectorsInfoCardBlockProps";
import { createSpreadableBem } from "/Helpers/createBem";
import { ContentAreaType } from "/Models/Generated/ContentAreaType";

import { useId, useState } from "react";
import DownloadSimpleIcon from "/Components/Icons/DownloadSimpleIcon";
import EmailContactRow from "/Components/Shared/EmailContactRow/EmailContactRow";

const PictureWrapper = ({ image, bem }: { image: any; bem: any }) => {
  if (!image) return null;
  return (
    <div {...bem}>
      <Picture
        {...image}
        pictureBehaviour="Cover"
        noAlt={(image.altText ?? "") === ""}
      />
    </div>
  );
};

const DirectorsInfoCardBlock = ({
  blockHeading,
  name,
  title,
  image,
  departmentLink,
  email,
  emailPrefixLabel,
  emailCopiedText,
  emailCopyLabel,
  overviewText,
  cvText,
  expandCvByDefault,
  showCvButtonText,
  hideCvButtonText,
  pressImages,
  showPressImagesButtonText,
  hidePressImagesButtonText,
  pressImageDownloadText,
}: DirectorsInfoCardBlockProps) => {
  const bem = createSpreadableBem("directors-info-card-block");
  const [cvExpanded, setCvExpanded] = useState(Boolean(expandCvByDefault));
  const [pressImagesExpanded, setPressImagesExpanded] = useState(false);
  const cvId = useId();
  const pressId = useId();

  const hasCv = ContentArea.HasContent(cvText);
  const hasPressImages = (pressImages?.length ?? 0) > 0;
  const hasNameOrTitle = Boolean(name || title);
  const hasContactInfo = Boolean(
    (departmentLink?.href && departmentLink.text) || email,
  );
  const hasPersonInfo = hasNameOrTitle || hasContactInfo;
  const hasDetails = hasPersonInfo || hasCv || hasPressImages;
  const hasTop = Boolean(image) || hasDetails;
  const showCv = hasCv && cvExpanded;
  const showPressImages = hasPressImages && pressImagesExpanded;

  return (
    <section {...bem("")}>
      {overviewText && <p {...bem("__overview-text")}>{overviewText}</p>}
      {blockHeading && <h2 {...bem("__block-heading")}>{blockHeading}</h2>}

      <div {...bem("__content")}>
        {hasTop && (
          <div {...bem("__top")}>
            <PictureWrapper image={image} bem={bem("__image")} />

            {hasDetails && (
              <div {...bem("__details")}>
                {hasPersonInfo && (
                  <div {...bem("__person-info")}>
                    {hasNameOrTitle && (
                      <div {...bem("__name-title")}>
                        {name && <h2 {...bem("__name")}>{name}</h2>}
                        {title && <p {...bem("__title")}>{title}</p>}
                      </div>
                    )}

                    {hasContactInfo && (
                      <ul {...bem("__contact-list")}>
                        {departmentLink?.href && departmentLink.text && (
                          <li {...bem("__contact-link")}>
                            <Link {...departmentLink} size="x-small" hasUnderline />
                          </li>
                        )}
                        {email && (
                          <li {...bem("__contact-item")}>
                             <EmailContactRow
                               email={email}
                               displayText={emailPrefixLabel ? `${emailPrefixLabel} ${email}` : email}
                               copiedText={emailCopiedText}
                               copyLabel={emailCopyLabel}
                             />
                          </li>
                        )}
                      </ul>
                    )}
                  </div>
                )}

                {(hasCv || hasPressImages) && (
                  <div {...bem("__toggle-row")}>
                    {hasCv && (
                      <ButtonWithIcon
                        icon={cvExpanded ? MinusCircleIcon : PlusCircleIcon}
                        iconPlacement="before"
                        onClickHandler={() => setCvExpanded((prev) => !prev)}
                        text={cvExpanded ? hideCvButtonText : showCvButtonText}
                        ariaExpanded={cvExpanded}
                        ariaControls={cvId}
                      />
                    )}
                    {hasPressImages && (
                      <ButtonWithIcon
                        icon={
                          pressImagesExpanded ? MinusCircleIcon : PlusCircleIcon
                        }
                        iconPlacement="before"
                        onClickHandler={() =>
                          setPressImagesExpanded((prev) => !prev)
                        }
                        text={
                          pressImagesExpanded
                            ? hidePressImagesButtonText
                            : showPressImagesButtonText
                        }
                        ariaExpanded={pressImagesExpanded}
                        ariaControls={pressId}
                      />
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {showCv && (
          <div id={cvId} {...bem("__cv-text")}>
            <ContentArea
              {...cvText}
              contentAreaType={
                cvText?.contentAreaType ?? ContentAreaType.RichText
              }
              richText={{ theme: "cv" }}
            />
          </div>
        )}

        {showCv && showPressImages && <div {...bem("__separator")} />}

        {showPressImages && (
          <div id={pressId} {...bem("__press-images")}>
            <div {...bem("__press-images-row")}>
              <div {...bem("__press-image-cards")}>
                {pressImages?.map((pressImage, idx) => (
                  <div key={idx} {...bem("__press-image-item")}>
                    <PictureWrapper
                      image={pressImage.image}
                      bem={bem("__press-image")}
                    />
                    {(pressImage.downloadHref ||
                      pressImage.image?.copyright) && (
                      <div {...bem("__press-image-download")}>
                        {pressImage.downloadHref && (
                          <Link
                            href={pressImage.downloadHref}
                            text={pressImageDownloadText}
                            isExternal={false}
                            icon={DownloadSimpleIcon}
                            iconPlacement="after"
                            size="x-small"
                            iconSize="16"
                            download
                          />
                        )}
                        {pressImage.image?.copyright && (
                          <div {...bem("__press-image-photo-by")}>
                            {pressImage.image?.copyrightLabel && (
                              <p {...bem("__press-image-photo-label")}>
                                {pressImage.image.copyrightLabel}
                              </p>
                            )}
                            <p {...bem("__press-image-caption")}>
                              {pressImage.image.copyright}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default DirectorsInfoCardBlock;
